import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AppButton, AppPagination, AppTable, AppTextArea, AppTooltip } from '@/components/atoms';
import { EditFill } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { EMPTY_TEXT_FILTERED } from '@/constants';
import { getServiceTypeLabel } from '@/constants/serviceType';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import useAppToast from '@/hooks/useAppToast';
import dayjs from 'dayjs';
import type {
  OrderRezeptSheetListItem,
  OrderRezeptSheetDetailItem,
} from '@/services/admin/order-rezept-sheets';
import { convGtJDate, convGtJDateShort } from '@/utils/dateConverter';
import { getSiteTypeShortCode } from '@/shared/constants/siteTypes';

/**
 * Flattened row for the table.
 * - type='detail': one detail line within an order group
 * - type='summary': trailing row per multi-detail order showing 合計
 */
export interface FlattenedRow {
  key: string;
  type: 'detail' | 'summary';
  orderId: number;
  clinicId: number;
  isFirstOfOrder: boolean;
  isLastDetail: boolean;
  isSingleDetail: boolean;
  clinic_number: string | null;
  clinic_name: string | null;
  site_type: string | null;
  service_type: string | null;
  order_date: string;
  order_no: number;
  detail?: OrderRezeptSheetDetailItem;
  total: number;
  delivery_fee: number;
}

type Meta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type EditedDetail = {
  ship_date: string | null;
  remarks: string | null;
};

type Props = {
  rows: OrderRezeptSheetListItem[];
  loading: boolean;
  meta: Meta;
  defaultPerPage: number;
  canUseSiteTypeFeatures?: boolean;
  tenantNameBySiteType?: Map<string, string>;
  editedDetails: Map<number, EditedDetail>;
  onEditDetail: (detailId: number, field: 'ship_date' | 'remarks', value: string | null) => void;
  onChangePage: (page: number) => void;
  onUpdateShipDate: () => void;
  isUpdating: boolean;
};

/**
 * Flatten orders into table rows. Each order_rezept_sheet_id is its own group:
 * - Single-detail order → 1 detail row (合計 inline)
 * - Multi-detail order → N detail rows + 1 trailing summary row with 合計
 */
export function flattenOrders(orders: OrderRezeptSheetListItem[]): FlattenedRow[] {
  const result: FlattenedRow[] = [];

  for (const order of orders) {
    const details = order.details ?? [];
    if (details.length === 0) continue;

    const isSingle = details.length === 1;

    details.forEach((detail, idx) => {
      result.push({
        key: `d-${detail.id}`,
        type: 'detail',
        orderId: order.id,
        clinicId: order.clinic_id,
        isFirstOfOrder: idx === 0,
        isLastDetail: idx === details.length - 1,
        isSingleDetail: isSingle,
        clinic_number: order.clinic_number,
        clinic_name: order.clinic_name,
        site_type: order.site_type,
        service_type: order.service_type,
        order_date: order.order_date,
        order_no: order.order_no,
        detail,
        total: order.total,
        delivery_fee: order.delivery_fee,
      });
    });

    if (!isSingle) {
      result.push({
        key: `s-${order.id}`,
        type: 'summary',
        orderId: order.id,
        clinicId: order.clinic_id,
        isFirstOfOrder: false,
        isLastDetail: false,
        isSingleDetail: false,
        clinic_number: null,
        clinic_name: null,
        site_type: null,
        service_type: null,
        order_date: '',
        order_no: order.order_no,
        total: order.total,
        delivery_fee: order.delivery_fee,
      });
    }
  }

  return result;
}

const fmtNum = (n: number | null | undefined): string => {
  if (n == null) return '';
  return n.toLocaleString('ja-JP');
};

const fmtWareki = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  return d.isValid() ? convGtJDate(d) : dateStr;
};

const fmtWarekiShort = (dateStr: string | null | undefined): string => {
  return convGtJDateShort(dateStr);
};

const dayFormat = parseFormat(JA_JP.format);

/** Convert RA2NDatePicker wareki string → YYYY-MM-DD for API */
function ra2nDateToYYYYMMDD(value: string): string {
  if (!value?.trim()) return '';
  const d = parseDate(value, dayFormat);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const isSummary = (r: FlattenedRow) => r.type === 'summary';
const isFirst = (r: FlattenedRow) => r.isFirstOfOrder;

const OrderRezeptSheetsTableSection: React.FC<Props> = ({
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures = false,
  tenantNameBySiteType = new Map(),
  editedDetails,
  onEditDetail,
  onChangePage,
  onUpdateShipDate,
  isUpdating,
}) => {
  const { colors } = useTheme();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'super-admin';
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useAppToast();
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const flatRows = useMemo(() => flattenOrders(rows), [rows]);

  useEffect(() => {
    if (editedDetails.size === 0) setHasAttemptedSubmit(false);
  }, [editedDetails]);

  const handleClickUpdate = useCallback(() => {
    setHasAttemptedSubmit(true);
    for (const row of flatRows) {
      if (row.type !== 'detail' || !row.detail) continue;
      const edited = editedDetails.get(row.detail.id);
      if (!edited) continue;
      const shipDate = edited.ship_date;
      const requestDate = row.detail.request_delivery_date;
      if (shipDate && requestDate && shipDate < requestDate) {
        toast.error('エラーが発生しました。');
        return;
      }
    }
    onUpdateShipDate();
  }, [flatRows, editedDetails, onUpdateShipDate, toast]);

  const getEdited = useCallback(
    (id: number, field: 'ship_date' | 'remarks', original: string | null) => {
      const e = editedDetails.get(id);
      return e ? e[field] : original;
    },
    [editedDetails]
  );

  const onDateChange = useCallback(
    (id: number) => (value: string) => {
      const iso = ra2nDateToYYYYMMDD(value);
      onEditDetail(id, 'ship_date', iso || null);
    },
    [onEditDetail]
  );

  const columns = useMemo(
    () => [
      // ── 治療院ID: {clinic_number}_{clinic_name}, first detail of each order ──
      {
        title: '治療院ID',
        key: 'clinic_name',
        width: 80,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          const number = r.clinic_number ?? '';
          const name = r.clinic_name ?? '';
          if (!number && !name) return '';
          return `${number}_${name}`;
        },
      },
      // ── サイト区分: first detail of each order (super-admin/medix only) ──
      {
        title: 'サイト',
        key: 'site_type',
        hidden: !canUseSiteTypeFeatures,
        width: 40,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          if (!r.site_type) return '';
          return (
            getSiteTypeShortCode(r.site_type) ??
            tenantNameBySiteType.get(r.site_type) ??
            r.site_type
          );
        },
      },
      // ── 受注日: R08.01.18 format ──
      {
        title: '受注日',
        key: 'order_date',
        width: 80,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          return fmtWarekiShort(r.order_date);
        },
      },
      // ── サービス区分 -> サービス: R2 / A2 ──
      {
        title: 'サービス',
        key: 'service_type',
        width: 50,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          if (!r.service_type) return '';
          const st = r.service_type.toUpperCase();
          if (st.includes('R2')) return 'R2';
          if (st.includes('A2')) return 'A2';
          if (st.includes('BOTH')) return 'A2, R2';
          return getServiceTypeLabel(r.service_type);
        },
      },
      // ── 用紙: 
      {
        title: '用紙',
        key: 'sheet_name',
        width: 130,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return `${r.detail.sheet_name}${r.detail.unit_amount ?? ''}`;
        },
      },
      // ── 数量:──
      {
        title: '数量',
        key: 'quantity',
        width: 50,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return r.detail.quantity;
        },
      },
      // ── 金額: ──
      {
        title: '金額',
        key: 'amount',
        width: 60,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return fmtNum(r.detail.price * r.detail.quantity);
        },
      },
      // ── 合計: 
      {
        title: '合計',
        key: 'total',
        width: 70,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r)) return fmtNum(r.total);
          if (r.isSingleDetail) return fmtNum(r.total);
          return null;
        },
      },
      // ── 配送料: ──
      {
        title: '配送料',
        key: 'delivery_fee',
        width: 60,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          return fmtNum(r.delivery_fee);
        },
      },
      // ── ケース同梱 ──
      {
        title: 'ケース同梱',
        key: 'is_include_case',
        width: 70,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return r.detail.is_include_case ? '同梱' : '';
        },
      },
      // ── 納品希望日: R08.01.18 format ──
      {
        title: '納品希望日',
        key: 'request_delivery_date',
        width: 80,
        onCell: () => ({
          style: {
            textAlign: 'right' as const,
          },
        }),
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return fmtWarekiShort(r.detail.request_delivery_date);
        },
      },
      // ── 出荷日: editable ──
      {
        title: '出荷日',
        key: 'ship_date',
        width: 130,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          const currentVal = getEdited(r.detail.id, 'ship_date', r.detail.ship_date);
          const requestDate = r.detail.request_delivery_date;
          const hasShipBeforeRequestError =
            hasAttemptedSubmit &&
            !!(currentVal && requestDate && currentVal < requestDate);
          return (
            <div className="pt-6">
              <RA2NDatePicker
                picker="day"
                value={currentVal ? fmtWareki(currentVal) : ''}
                onChange={onDateChange(r.detail.id)}
                placeholder=""
                className="w-full"
                disabled={isUpdating}
                error={hasShipBeforeRequestError}
                shortFormat={true}
              />
              <div className="mt-1 h-5 text-sm leading-5 text-red-500">
                {hasShipBeforeRequestError && (
                  <AppTooltip title="出荷日は納品希望日より後の日時を指定してください。">
                    <div className="truncate">
                      出荷日は納品希望日より後の日時を指定してください。
                    </div>
                  </AppTooltip>
                )}
              </div>
            </div>
          );
        },
      },
      // ── 備考: 
      {
        title: '備考',
        key: 'remarks',
        width: 130,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !r.detail) return null;
          return (
            <AppTextArea
              value={getEdited(r.detail.id, 'remarks', r.detail.remarks) ?? ''}
              onChange={e => onEditDetail(r.detail!.id, 'remarks', e.target.value || null)}
              maxLength={225}
              rows={1}
              size="small"
              className="resize-y"
              disabled={isUpdating}
            />
          );
        },
      },
      // ── 操作: 
      {
        title: '操作',
        key: 'action',
        width: 70,
        render: (_: unknown, r: FlattenedRow) => {
          if (isSummary(r) || !isFirst(r)) return null;
          return (
            <AppButton
              icon={<EditFill />}
              type="primary"
              className="!px-2"
              onClick={() =>
                navigate(`/${currentSite}/admin/order_rezept_sheets/${r.orderId}/edit`, {
                  state: { listSearch: location.search },
                })
              }
            >
              編集
            </AppButton>
          );
        },
      },
    ],
    [
      canUseSiteTypeFeatures,
      tenantNameBySiteType,
      currentSite,
      navigate,
      location.search,
      getEdited,
      onDateChange,
      onEditDetail,
      isUpdating,
      hasAttemptedSubmit,
    ]
  );

  const rowClassName = useCallback((record: FlattenedRow) => {
    if (record.type === 'summary') return 'bg-gray-100 border-b-2 border-gray-400';
    if (record.isLastDetail && record.isSingleDetail) return 'border-b-2 border-gray-400';
    return '';
  }, []);

  return (
    <div className="rounded-md border bg-white">
      <AppTable
        isAdminSite
        columns={columns}
        dataSource={flatRows}
        rowKey="key"
        loading={loading}
        rowClassName={rowClassName}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: EMPTY_TEXT_FILTERED }}
      />
      {meta && (
        <div className="p-4">
          <div className="flex justify-center mb-4">
            <AppButton
              type="primary"
              onClick={handleClickUpdate}
              loading={isUpdating}
              style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
              className="btn-ra2-hover"
            >
              出荷日更新
            </AppButton>
          </div>
          <div className="flex justify-center">
            <AppPagination
              current={meta.current_page}
              total={meta.total}
              pageSize={meta.per_page || defaultPerPage}
              hideOnSinglePage
              onChange={p => onChangePage(p)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderRezeptSheetsTableSection;
